import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { Chip, CircularProgress } from '@material-ui/core';
import { PrimaryButton } from 'components/buttons/v4';
import { getDifficultyColors } from 'components/reports/cells/ItemDifficulty';
import { BulkActionBar, SelectionCount, DeselectButton, createSelectionColumn } from 'components/tables/BulkSelectionElements';
import { ColumnDef, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import LoadingComponent from 'components/LoadingComponent';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import { SortArrow, TableHeaderContainer, TableHeaderText } from 'components/tables/learner-stats/elements';
import { TableSearchBar } from 'components/tables/TableSearchBar';
import { Color } from 'core';
import { useContentIntelligenceItems } from 'hooks/useContentIntelligenceItems';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { formatItemTemplate } from 'utils/formatItemTemplate';
import { pluralize } from 'utils/stringUtils';
import { useState, useEffect, useRef, useMemo } from 'react';
import { queryCache } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ContentIntelligenceFilters, ContentIntelligenceFilterChips } from './ContentIntelligenceFilters';
import { ContentIntelligenceItem, ContentIntelligenceFilters as FilterType } from './types';
import { ExtractToSetModal } from './ExtractToSetModal';
import { getContentTypeDisplayName } from 'components/modals/create-flow/shared/contentTypeUtils';

const MAX_EXTRACT_ITEMS = 500;

export const ContentIntelligenceTable = () => {
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [searchName, setSearchName] = useState<string>('');
  const [filters, setFilters] = useState<FilterType>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showExtractModal, setShowExtractModal] = useState(false);
  const selectedItemsMap = useRef(new Map<string, ContentIntelligenceItem>());

  const { t } = useTranslation();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;

  const {
    data,
    status: queryStatus,
    isFetching,
  } = useContentIntelligenceItems(primaryPartnerId, pageNumber, pageSize, sortColumn, sortDirection, searchName, filters);

  const items = useMemo(() => data?.data || [], [data?.data]);
  const meta = data?.meta;
  const totalCount = meta ? meta['total-count'] : 0;
  const totalPages = meta ? meta['total-pages'] : 0;

  // Sync selectedItemsMap with current page items and rowSelection
  useEffect(() => {
    items.forEach(item => {
      if (rowSelection[item.id]) {
        selectedItemsMap.current.set(item.id, item);
      }
    });
    selectedItemsMap.current.forEach((_, id) => {
      if (!rowSelection[id]) {
        selectedItemsMap.current.delete(id);
      }
    });
  }, [items, rowSelection]);

  // Clear selection when filters or search change; reset page for filters
  // (TableSearchBar handles its own page reset via resetPage prop)
  useEffect(() => {
    setRowSelection({});
    selectedItemsMap.current.clear();
    setPageNumber(1);
  }, [searchName, filters, sortColumn, sortDirection]);

  // Pagination handlers
  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const handleSortingChange = (sortBy: string) => {
    if (sortBy === sortColumn) {
      toggleSortDirection();
    } else {
      setSortColumn(sortBy);
      setSortDirection('desc');
    }
    setPageNumber(1);
  };

  const formatTimeToMastery = (seconds: number | null): string => {
    if (seconds == null) {
      return t('N/A');
    }
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.round(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.round((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  const selectedItems = Array.from(selectedItemsMap.current.values());
  const selectedCount = Object.keys(rowSelection).length;
  const exceedsLimit = selectedCount > MAX_EXTRACT_ITEMS;

  const handleExtractModalClose = (success?: boolean) => {
    setShowExtractModal(false);
    if (success) {
      queryCache.invalidateQueries('contentIntelligenceItems');
      setRowSelection({});
      selectedItemsMap.current.clear();
    }
  };

  const columns: ColumnDef<ContentIntelligenceItem>[] = [
    createSelectionColumn<ContentIntelligenceItem>(),
    {
      id: 'name',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <ItemNameCell>
            <ItemLink to={`/nav/sets/${rowData.setId}/items?itemId=${rowData.id}`}>
              {rowData.name?.slice(0, 500)}
            </ItemLink>
          </ItemNameCell>
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Item')}
          columnId="name"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('name')}
          isSortable={false}
        />
      ),
    },
    {
      id: 'setName',
      accessorFn: (row) => row.setName,
      cell: ({ row }) => (
        <>
          <TypePill>{getContentTypeDisplayName(row.original.goalType, true)}</TypePill>{' '}
          <ItemLink to={`/nav/sets/${row.original.setId}/items?itemId=${row.original.id}`}>{row.original.setName}</ItemLink>
        </>
      ),
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'left' }}>
          <TableHeaderText>{t('Content Set')}</TableHeaderText>
        </TableHeaderContainer>
      ),
    },
    {
      id: 'template',
      accessorFn: (row) => row.template,
      cell: ({ row }) => (
        <TypePill>{formatItemTemplate(row.original.template)}</TypePill>
      ),
      header: () => (
        <ColumnHeader
          title={t('Type')}
          columnId="template"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('template')}
          isSortable={false}
        />
      ),
    },
    {
      id: 'difficulty',
      accessorFn: (row) => row.difficulty,
      cell: ({ row }) => {
        const rowData = row.original;
        const difficulty = rowData.readableDifficulty ?? 'N/A';
        const styles = getDifficultyColors(difficulty);
        return <Chip style={{ ...styles, borderRadius: '6px' }} variant="outlined" label={difficulty} />;
      },
      header: () => (
        <ColumnHeader
          title={t('Difficulty')}
          columnId="difficulty"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('difficulty')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'timeToMastery',
      accessorFn: (row) => row.timeToMastery,
      cell: ({ row }) => (
        <span>{formatTimeToMastery(row.original.timeToMastery)}</span>
      ),
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'left' }}>
          <TableHeaderText>{t('Time to Mastery')}</TableHeaderText>
        </TableHeaderContainer>
      ),
    },
    {
      id: 'learnerCount',
      accessorFn: (row) => row.learnerCount,
      cell: ({ row }) => (
        <span>{row.original.learnerCount}</span>
      ),
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'left' }}>
          <TableHeaderText>{t('Learners')}</TableHeaderText>
        </TableHeaderContainer>
      ),
    },
    {
      id: 'created_at',
      accessorFn: (row) => row.createdAt,
      cell: ({ row }) => (
        <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
      ),
      header: () => (
        <ColumnHeader
          title={t('Created')}
          columnId="created_at"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('created_at')}
          isSortable={true}
        />
      ),
    },
  ];

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
  });

  if (queryStatus === 'loading') {
    return <LoadingComponent component={<></>} isLoading={true} />;
  }

  const noItemsMessage = (
    <NoItemsContainer>
      <h2>{t('No Items Found')}</h2>
      <p>{t('Try adjusting your search or filters to find content items.')}</p>
    </NoItemsContainer>
  );

  const errorMessage = (
    <ErrorContainer>
      <h2>{t('Error Loading Data')}</h2>
      <p>{t('There was a problem loading the content intelligence data. Please try refreshing the page.')}</p>
    </ErrorContainer>
  );

  const tableContent =
    queryStatus === 'error' ? (
      errorMessage
    ) : (
      <>
        <StyledTable>
          <TableHeader table={table} handleHeaderClick={() => {}} />
          <TableBody table={table} />
        </StyledTable>
        {items.length === 0 && totalCount === 0 && noItemsMessage}
        {totalCount > 0 && (
          <Pagination
            totalCount={totalCount}
            page={pageNumber}
            pageSize={pageSize}
            goToPageOne={goToPageOne}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
            goToLastPage={goToLastPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </>
    );

  return (
    <div>
      <SearchAndFilterContainer>
        <SearchContainer>
          <TableSearchBar
            value={searchName}
            onChange={setSearchName}
            resetPage={() => setPageNumber(1)}
            placeholder={t('Search items')}
          />
        </SearchContainer>
        <ContentIntelligenceFilters filters={filters} onFiltersChange={setFilters} />
      </SearchAndFilterContainer>
      <ContentIntelligenceFilterChips filters={filters} onFiltersChange={setFilters} isLoading={isFetching} />
      {selectedCount > 0 && (
        <BulkActionBar>
          <SelectionCount>{pluralize(selectedCount, 'item')} {t('selected')}</SelectionCount>
          {exceedsLimit && (
            <LimitWarning>{t('Maximum {{max}} items per set', { max: MAX_EXTRACT_ITEMS })}</LimitWarning>
          )}
          <PrimaryButton click={() => setShowExtractModal(true)} disabled={exceedsLimit}>
            {t('Extract to New Set')}
          </PrimaryButton>
          <DeselectButton onClick={() => { setRowSelection({}); selectedItemsMap.current.clear(); }}>{t('Deselect All')}</DeselectButton>
        </BulkActionBar>
      )}
      <TableWrapper>
        {isFetching && (
          <LoadingSpinner>
            <CircularProgress size={20} />
          </LoadingSpinner>
        )}
        <TableContainer>{tableContent}</TableContainer>
      </TableWrapper>
      {showExtractModal && (
        <ExtractToSetModal
          items={selectedItems}
          onClose={handleExtractModalClose}
        />
      )}
    </div>
  );
};

interface ColumnHeaderProps {
  title: string;
  columnId: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
  isSortable?: boolean;
}

const ColumnHeader = ({ title, columnId, sortColumn, sortDirection, onSort, isSortable = false }: ColumnHeaderProps) => {
  return (
    <TableHeaderContainer
      style={Object.assign({ justifyContent: 'left', whiteSpace: 'nowrap' }, isSortable ? { cursor: 'pointer' } : {})}
      onClick={isSortable ? onSort : undefined}
    >
      <TableHeaderText>{title}</TableHeaderText>
      {sortColumn === columnId && (
        <SortArrow icon={sortDirection === 'desc' ? faArrowDown : faArrowUp} />
      )}
    </TableHeaderContainer>
  );
};

const TableWrapper = styled.div`
  position: relative;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 12px;
  left: 8px;
  z-index: 5;
`;

const TableContainer = styled.div``;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SearchAndFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid ${Color.lightGray};
`;

const SearchContainer = styled.div`
  flex: 1;
`;

const ItemNameCell = styled.div`
  max-width: 300px;
`;

const ItemLink = styled(Link)`
  font-weight: 600;
  color: ${Color.primaryBlack};
  text-decoration: none;

  &:hover {
    text-decoration: underline !important;
    color: ${Color.primaryBlue} !important;
  }

  &:focus {
    outline: 2px solid ${Color.primaryBlue};
    outline-offset: 2px;
  }
`;

const TypePill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  background-color: ${Color.veryLightGray};
  color: ${Color.textGray};
`;

const NoItemsContainer = styled.div`
  color: ${Color.darkGray};
  text-align: center;
  padding: 48px 0;

  h2 {
    margin-bottom: 8px;
  }
`;

const ErrorContainer = styled.div`
  color: ${Color.red};
  text-align: center;
  padding: 48px 0;

  h2 {
    margin-bottom: 8px;
  }
`;

const LimitWarning = styled.span`
  font-size: 13px;
  color: ${Color.red};
`;
